import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { EditRequest, StudioEffect } from "@loumy/contracts";
import { api } from "../api/client";
import { getBrowserOps, MAX_REKEY_TOTAL_PIXELS } from "../browser-ops/client";
import { decodeVideoFrames, MAX_DECODE_FRAMES, probeVideoMeta } from "../browser-ops/videoDecode";
import { estimateStudioEditCredits } from "../pricing/preview";
import { downloadResult, resultFilename } from "../results/download";
import { useUiStore } from "../state/uiStore";
import { Button, Icon, ProgressBar, Spinner } from "../ui";
import { canExportWebm as effectsAllowWebm } from "./effectStack";
import { GridBadge } from "./GridBadge";
import { GridDialog } from "./GridDialog";
import { clampGrid } from "./gridPlan";
import { OpStackPanel } from "./OpStackPanel";
import { StudioCanvas } from "./StudioCanvas";
import { Timeline } from "./Timeline";
import { TransportBar } from "./TransportBar";
import {
  allEffects,
  buildClipGroups,
  deriveSteps,
  keptRanges,
  outputFrameCount,
  resolveOutputIndices,
  type StudioResultKind,
} from "./studioPlan";
import { useStudioStore, type StudioGrid, type StudioSourceKind } from "./studioStore";

/** Die (aktiven) chroma-key-Parameter aus dem Effekt-Stack — für params/WebM-Settings/RIFE. */
function activeKeyParams(effects: StudioEffect[]): { keyColor: "auto" | "green" | "blue"; tolerance: number } {
  const k = effects.find((e) => e.enabled && e.type === "chroma-key");
  return k && k.type === "chroma-key" ? { keyColor: k.keyColor, tolerance: k.tolerance } : { keyColor: "auto", tolerance: 20 };
}

/**
 * Codex-P1: der `speed`-Effekt ändert die Frame-ANZAHL des Effekt-Outputs gegenüber der Timeline-
 * (Quell-)Sequenz. Timeline + Transport bleiben QUELL-indexiert (Schnitt/Playhead/K=Cut bleiben
 * konsistent); Anzeige + Einzelframe-Save bilden den Playhead PROPORTIONAL in die (ggf. kürzere/
 * längere) Effekt-Sequenz ab. Ohne Frame-zahl-ändernde Effekte ist die Abbildung die Identität.
 */
function mapProportional(i: number, fromCount: number, toCount: number): number {
  if (toCount <= 1) return 0;
  if (fromCount <= 1) return Math.max(0, Math.min(toCount - 1, Math.trunc(i)));
  return Math.max(0, Math.min(toCount - 1, Math.round((i * (toCount - 1)) / (fromCount - 1))));
}

// §9.17/D139 — Asset-Studio (Vollbild). Eine Quelle laden (Deep-Link `?asset=&kind=&fps=` ODER
// Dropzone), als Frame-Sequenz behandeln (Bild=1, Video=N@fps, Spritesheet=detect→unpack),
// non-destruktiv schneiden/keyen LIVE via Pyodide (ops.py-SSOT), Save = EIN POST /v1/edits
// (Sheet-PNG/Einzelframe-PNG) bzw. WebM-Job (studio-export). Frames liegen in Refs (nicht im Store).

function isTextEntryTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    // Range-Slider (Timeline-Scrubber/Toleranz) sollen die Shortcuts NICHT schlucken — nur
    // echte Text-Eingaben blockieren (sonst sind Space/←/→ nach einem Slider-Klick tot).
    const type = (el as HTMLInputElement).type;
    return type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button";
  }
  return false;
}

/**
 * KEY-SPEZIFISCHES Nachgeben: ein fokussierter Button/Checkbox will Space/Enter selbst
 * (Aktivieren), ein fokussierter Range-Slider will ←/→ selbst (Wert ändern). Nur DIESE Taste
 * wird dem Control überlassen — alle anderen Shortcuts bleiben global aktiv (Codex-P2).
 */
function shouldYieldToFocusedControl(e: KeyboardEvent): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  const type = tag === "INPUT" ? (el as HTMLInputElement).type : "";
  if ((tag === "BUTTON" || type === "checkbox") && (e.key === " " || e.key === "Enter")) return true;
  if (type === "range" && (e.key === "ArrowLeft" || e.key === "ArrowRight")) return true;
  return false;
}

/**
 * Erste gedroppte Datei, die ein Bild oder Video ist (der `<input>`-Picker filtert über `accept`,
 * ein nativer Drop tut das NICHT — wir spiegeln den `image/*,video/*`-Filter hier). Der Server
 * validiert die ECHTEN Bytes ohnehin per Magic-Byte-Sniffing (D61); diese Prüfung ist nur sofortiges
 * UX-Feedback gegen offensichtlich falsche Dateitypen.
 */
function firstMediaFile(files: FileList): File | null {
  for (const f of Array.from(files)) {
    if (f.type.startsWith("image/") || f.type.startsWith("video/")) return f;
  }
  return null;
}

async function blobToImageData(blob: Blob): Promise<ImageData> {
  const bmp = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas-2D nicht verfügbar");
    ctx.drawImage(bmp, 0, 0);
    return ctx.getImageData(0, 0, bmp.width, bmp.height);
  } finally {
    bmp.close();
  }
}

export function StudioShell() {
  const [params] = useSearchParams();
  const selectedFolderId = useUiStore((s) => s.selectedFolderId);
  const store = useStudioStore();
  const {
    status,
    error,
    sourceAssetId,
    sourceKind,
    totalFrames,
    fps,
    grid,
    segments,
    playhead,
    playing,
    selectedSegmentId,
    seam,
    loop,
    resultKind,
  } = store;
  // §9.17 — Effekte gelten PRO Clip: `flatEffects` (alle Clips, flach) speist Preis-Steps/WebM-Sperre/
  // Provenance; `selectedEffects` ist der Stack des im Panel bearbeiteten Clips.
  const flatEffects = useMemo(() => allEffects(segments), [segments]);
  const selectedEffects = useMemo(
    () => segments.find((s) => s.id === selectedSegmentId)?.effects ?? [],
    [segments, selectedSegmentId],
  );
  const hasEnabledEffects = useMemo(() => flatEffects.some((e) => e.enabled), [flatEffects]);
  // Globaler Transparenz-Crop (nach dem Padden), wenn IRGENDEIN behaltener Clip gekeyt ist — EIN
  // gemeinsamer BBox ⇒ konsistentes Framing (kein Pro-Clip-Sprung). Speist alle Worker-Calls.
  const cropTransparent = useMemo(() => flatEffects.some((e) => e.enabled && e.type === "chroma-key"), [flatEffects]);
  // Pro-Clip-Effekt-Stacks der BEHALTENEN Clips (für die WebM-Reproduzierbarkeitsprüfung).
  const keptClipEffects = useMemo(() => segments.filter((s) => s.keep).map((s) => s.effects), [segments]);

  const framesRef = useRef<ImageData[]>([]); // Quell-Frames (volle Sequenz, Identitäts-Reihenfolge)
  const previewRef = useRef<ImageData[] | null>(null); // Effekt-Output-Frames (LIVE-Vorschau des Stacks)
  const opsHashRef = useRef<string>("");
  const loadAbortRef = useRef<AbortController | null>(null); // Codex-P2-2: Decode/Fetch beim Verlassen abbrechen
  // Original-Sheet-BYTES (nur Bild/Sheet-Quellen) für ein erneutes Entpacken bei Grid-Korrektur — der
  // komprimierte Blob (kein dekodiertes RGBA) ⇒ billig zu halten; applyGrid dekodiert on demand, statt
  // das Sheet bei großen Quellen erneut vom Server zu holen (das war der langsame Teil).
  const originalBlobRef = useRef<Blob | null>(null);
  const [renderTick, forceRender] = useState(0);
  const [bootStage, setBootStage] = useState<string>("");
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  // Original-Sheet (ObjectURL + Pixelmaße) für das Live-Raster-Overlay im Grid-Dialog (D139-Amend).
  // Nur für Bild/Sheet-Quellen gesetzt (Video hat kein Grid-Konzept). Cleanup s. useEffect unten.
  const [sheet, setSheet] = useState<{ url: string; width: number; height: number } | null>(null);
  const [busy, setBusy] = useState(false);
  // D169: Phasen-Fortschritt der mehrstufigen Save/Download/Export-Operationen (im OpStackPanel als
  // ProgressBar). `value`/`max` = abgeschlossene/gesamte Phasen (determinate); `value` weggelassen
  // ⇒ indeterminate (ein Schritt unbekannter Dauer, z.B. WebM-Job-Start). Rein UI, KEIN Server-Push:
  // die Phasen sind synchrone await-Schritte, daher hier manuell gesetzt und im finally geräumt.
  const [progress, setProgress] = useState<{ label: string; value?: number; max?: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false); // Drag-&-Drop-Hover über der Dropzone (Empty-State)

  // ObjectURL des Sheet-Previews bei Wechsel/Unmount freigeben (sonst Leak je Load).
  useEffect(() => {
    const u = sheet?.url;
    return () => {
      if (u) URL.revokeObjectURL(u);
    };
  }, [sheet?.url]);

  const outputIndices = useMemo(() => resolveOutputIndices(segments, totalFrames), [segments, totalFrames]);
  const outputCount = outputIndices.length;

  // ── Quelle laden (Deep-Link oder Dropzone) ──────────────────────────────────
  const loadFromBlob = useCallback(
    async (
      blob: Blob,
      kind: StudioSourceKind,
      srcFps: number,
      srcAssetId: string | null,
      signal?: AbortSignal,
      knownGrid?: { cols: number; rows: number; frameCount: number } | null,
    ) => {
      const ops = getBrowserOps();
      store.setStatus("loading");
      previewRef.current = null;
      try {
        const { opsHash } = await ops.ensureReady(setBootStage);
        opsHashRef.current = opsHash;

        let frames: ImageData[];
        let detectedGrid: StudioGrid | null = null;
        let resKind: StudioResultKind = "spritesheet";
        let sheetImage: ImageData | null = null; // Original-Sheet für das Grid-Overlay (Nicht-Video)

        if (kind === "video" || kind === "animation") {
          const meta = await probeVideoMeta(blob, signal);
          const rawCount = Math.min(Math.ceil(meta.duration * srcFps), MAX_DECODE_FRAMES);
          // Pixel-Budget: ggf. herunterskalieren, damit Quelle (Main-Thread) + Worker passen.
          const budget = MAX_REKEY_TOTAL_PIXELS / 2; // Hälfte: wir halten 2 Kopien (Main + Worker)
          const full = meta.width * meta.height * Math.max(1, rawCount);
          const scale = full > budget ? Math.max(0.1, Math.sqrt(budget / full)) : 1;
          frames = [];
          for await (const f of decodeVideoFrames(blob, { fps: srcFps, maxFrames: rawCount, scale, signal })) {
            frames.push(f);
          }
          if (frames.length === 0) throw new Error("Video lieferte keine Frames");
        } else {
          // Bild / Spritesheet: ein Bild dekodieren, Grid erkennen, ggf. entpacken. Ist das Grid
          // bereits bekannt (Sheet aus dem Tool-Result geöffnet — pack.meta im Deep-Link), das
          // detect_grid-Raten überspringen und direkt mit dem bekannten Raster entpacken; sonst
          // (Dropzone/Bild) wie gehabt erkennen.
          const img = await blobToImageData(blob);
          sheetImage = img;
          // Codex-P2: knownGrid kommt NUR aus einem kind=spritesheet-Deep-Link (= das EXAKTE
          // pack.meta-Raster) ⇒ direkt vertrauen, kein detect_grid. Ein theoretisches 1×1-Sheet
          // fällt unten korrekt auf den Einzelbild-Pfad (g.cols*g.rows > 1 ist dort false).
          const g: { cols: number; rows: number; frameCount: number; method: "alpha" | "chroma" | "none"; detectedKey: number[] | null } =
            knownGrid
              ? { cols: knownGrid.cols, rows: knownGrid.rows, frameCount: knownGrid.frameCount, method: "alpha", detectedKey: null }
              : await ops.studioDetectGrid(img);
          if (g.method !== "none" && g.cols * g.rows > 1) {
            // Sheet → in den Worker laden, entpacken, Frames zurückholen (SSOT spritesheet_unpack).
            await ops.studioLoad([img]);
            await ops.studioUnpack(g.cols, g.rows, g.frameCount);
            frames = await ops.studioPreview([], undefined);
            detectedGrid = { cols: g.cols, rows: g.rows, frameCount: g.frameCount, method: g.method, detectedKey: g.detectedKey ?? null };
            resKind = "spritesheet";
          } else {
            frames = [img];
            detectedGrid = { cols: 1, rows: 1, frameCount: 1, method: "none", detectedKey: g.detectedKey ?? null };
            resKind = "image"; // Einzelbild → Default Einzelframe-Ergebnis
          }
        }

        // Codex-P1-2: Abort-Guard auch VOR dem finalen Worker-Write — spart den teuersten Puffer-
        // Füll-Schritt eines bereits überholten (abgebrochenen) Loads. (Der Browser-Ops-Client ist
        // FIFO-seriell; einen tieferen Interleave der mutierenden Zwischen-Ops eines Vorgängers
        // deckt dieser Guard nicht ab — pre-existing Einschränkung des loadFromBlob-Pfads, PRD §13.
        // Ein neuer Load resettet den Puffer ohnehin via st-begin in studioLoad.)
        if (signal?.aborted) return;
        // Worker-Puffer mit GENAU diesen Frames füllen (WYSIWYG: Vorschau == Worker == Save).
        await ops.studioLoad(frames);
        // … und VOR dem React-State-Commit (store + sheet): sonst könnte ein überholter Load store +
        // sheet nachträglich zurückschreiben und einen frisch gestarteten Load überrennen.
        if (signal?.aborted) return;
        framesRef.current = frames;
        // Original-Sheet-Bytes für eine spätere Grid-Korrektur behalten (nur Nicht-Video — Video hat
        // kein Grid). sheetImage != null ⇔ Bild/Sheet-Quelle ⇔ `blob` IST das Original-Sheet.
        originalBlobRef.current = sheetImage ? blob : null;
        const first = frames[0]!;
        store.loaded({
          sourceAssetId: srcAssetId,
          sourceKind: kind,
          totalFrames: frames.length,
          frameWidth: first.width,
          frameHeight: first.height,
          fps: srcFps,
          grid: detectedGrid,
          resultKind: resKind,
          loop: true,
        });
        // Codex-P1-2 / User-Direktive „nicht erkannt → manuelle Grid-UI": wurde die Quelle EXPLIZIT
        // als Spritesheet geöffnet (Deep-Link kind=spritesheet), die Erkennung aber scheiterte
        // (method='none'), den manuellen Grid-Dialog automatisch öffnen. Für gewöhnliche Bild-
        // Uploads NICHT auto-öffnen (eine Foto-Quelle ist korrekt ein Einzelbild) — dort heilt das
        // immer sichtbare Grid-Badge (s. Kopfleiste).
        if (kind === "spritesheet" && detectedGrid?.method === "none") setGridDialogOpen(true);
        // Original-Sheet als ObjectURL fürs Grid-Overlay (erst hier — nach allen awaits, kein Leak
        // bei abgebrochenem Load). Video-Quellen haben kein Grid ⇒ kein Sheet.
        setSheet(sheetImage ? { url: URL.createObjectURL(blob), width: sheetImage.width, height: sheetImage.height } : null);
        forceRender((n) => n + 1);
      } catch (e) {
        // Codex-P2-2: ein ABGEBROCHENER Load (Unmount/neuer Load) darf den Status NICHT auf
        // 'empty' setzen — sonst überrennt der alte Lauf einen bereits gestarteten neuen.
        if (signal?.aborted) return;
        store.setStatus("empty", e instanceof Error ? e.message : String(e));
      }
    },
    [store],
  );

  // Deep-Link beim Mount.
  useEffect(() => {
    const asset = params.get("asset");
    if (!asset) return;
    const kind = (params.get("kind") as StudioSourceKind | null) ?? "image";
    const srcFps = Number(params.get("fps")) || 24;
    // D139 — bekanntes Sheet-Grid aus dem Deep-Link (das Tool-Result kennt cols/rows/frames aus
    // pack.meta): direkt entpacken, statt detect_grid auf dem gepackten Sheet raten zu lassen.
    const cols = Number(params.get("cols"));
    const rows = Number(params.get("rows"));
    const frames = Number(params.get("frames"));
    // Codex-P1-1: Deep-Link-Grid-Parameter sind nicht vertrauenswürdig (manipulierbar) — über
    // clampGrid auf gültige Bereiche ([1,64], frameCount ≤ cols×rows) klemmen, bevor sie ungebremst
    // an studioUnpack/den Worker gehen. Nicht-endliche/≤0 ⇒ kein knownGrid (detect_grid übernimmt).
    // Codex-P2: nur ein kind=spritesheet-Deep-Link bringt ein bekanntes Raster mit — sonst (Bild/
    // Video) ignorieren, damit eine manipulierte URL keinen Bild-Blob durch studioUnpack jagt.
    const knownGrid =
      kind === "spritesheet" &&
      Number.isFinite(cols) && Number.isFinite(rows) && Number.isFinite(frames) && cols > 0 && rows > 0 && frames > 0
        ? clampGrid(cols, rows, frames)
        : null;
    const ac = new AbortController();
    loadAbortRef.current?.abort();
    loadAbortRef.current = ac;
    void (async () => {
      try {
        const blob = await api.assetBytes(asset, ac.signal);
        if (!ac.signal.aborted) await loadFromBlob(blob, kind, srcFps, asset, ac.signal, knownGrid);
      } catch (e) {
        if (!ac.signal.aborted) store.setStatus("empty", e instanceof Error ? e.message : String(e));
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Worker-Puffer + laufenden Decode/Fetch beim Verlassen freigeben/abbrechen.
  useEffect(() => {
    return () => {
      loadAbortRef.current?.abort();
      originalBlobRef.current = null; // Codex-P2: gehaltene Original-Bytes beim Verlassen freigeben
      void getBrowserOps().studioRelease();
    };
  }, []);

  // Dropzone-Upload: erst hochladen (Save braucht sourceAssetId), dann lokal laden.
  const onUpload = useCallback(
    async (file: File) => {
      store.setStatus("loading");
      const ac = new AbortController();
      loadAbortRef.current?.abort();
      loadAbortRef.current = ac;
      try {
        const asset = await api.uploadAsset(file, selectedFolderId ?? null);
        const kind = (asset.kind as StudioSourceKind) ?? "image";
        if (!ac.signal.aborted) await loadFromBlob(file, kind, 24, asset.id, ac.signal);
      } catch (e) {
        if (!ac.signal.aborted) store.setStatus("empty", e instanceof Error ? e.message : String(e));
      }
    },
    [loadFromBlob, selectedFolderId, store],
  );

  // ── Effekt-Stack-LIVE-Vorschau (debounced) ──────────────────────────────────
  // Sind aktive Effekte im Stack, rendert der Worker die volle Effekt-Kette (forPreview:true =
  // ohne RIFE-Naht) und wir zeigen das Ergebnis; ohne aktive Effekte bleibt previewRef null und
  // der Canvas zeigt die rohen Output-Frames (Segment-Mathe im Main-Thread, kein Worker-Roundtrip).
  useEffect(() => {
    if (status !== "ready") return;
    if (!hasEnabledEffects) {
      previewRef.current = null;
      forceRender((n) => n + 1);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      const clips = buildClipGroups(segments, totalFrames, { withEffects: true });
      void getBrowserOps()
        .studioPreview(clips, undefined, cropTransparent)
        .then((frames) => {
          if (!cancelled) {
            previewRef.current = frames;
            forceRender((n) => n + 1);
          }
        })
        .catch((e) => {
          if (!cancelled) {
            previewRef.current = null;
            setNotice(`Vorschau fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
          }
        });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [status, hasEnabledEffects, segments, totalFrames, resultKind, cropTransparent]);

  // ── Playback (setInterval über die OUTPUT-Sequenz) ──────────────────────────
  useEffect(() => {
    if (!playing || outputCount === 0) return;
    const handle = setInterval(() => {
      const s = useStudioStore.getState();
      const oc = resolveOutputIndices(s.segments, s.totalFrames).length;
      if (oc === 0) return;
      s.setPlayhead((s.playhead + 1) % oc);
    }, 1000 / Math.max(1, fps));
    return () => clearInterval(handle);
  }, [playing, outputCount, fps]);

  // Playhead klemmen, wenn der Output schrumpft.
  useEffect(() => {
    if (playhead >= outputCount && outputCount > 0) store.setPlayhead(outputCount - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputCount]);

  // Tastatur (Space/←/→/K) — SELBST-ENTHALTEND: liest IMMER den frischen Store-State (keine
  // Stale-Closures über step/cutAtPlayhead/playhead) und registriert sich nur EINMAL (deps []),
  // sonst steppten ←/→ wegen veralteter Closures stets vom Initial-Playhead.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isTextEntryTarget(e.target) || shouldYieldToFocusedControl(e)) return;
      const s = useStudioStore.getState();
      if (s.status !== "ready") return;
      // Undo/Redo (⌘/Ctrl+Z, ⌘/Ctrl+⇧Z bzw. ⌘/Ctrl+Y) — vor der Transport-Kette, da eigene
      // Modifier-Kombis.
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }
      if (mod && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        s.redo();
        return;
      }
      const indices = resolveOutputIndices(s.segments, s.totalFrames);
      const oc = indices.length;
      if (e.key === " ") {
        e.preventDefault();
        s.setPlaying(!s.playing);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (oc === 0) return;
        s.setPlaying(false);
        const delta = e.key === "ArrowLeft" ? -1 : 1;
        s.setPlayhead((s.playhead + delta + oc) % oc);
      } else if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        const abs = indices[s.playhead];
        if (abs !== undefined) s.cutAtPlayhead(abs);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Geste-Grenze für Undo: ein Pointer-Drag (Slider/Trim feuert dabei viele Mutationen) endet beim
  // Loslassen ⇒ pointerup/-cancel schließen den Coalescing-Block, der nächste Drag wird ein eigener
  // Undo-Schritt (echte gestenbasierte History — kein bloßes Zeitfenster-Raten).
  useEffect(() => {
    const onUp = () => useStudioStore.getState().endGesture();
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const step = useCallback(
    (delta: number) => {
      if (outputCount === 0) return;
      store.setPlaying(false);
      const next = (playhead + delta + outputCount) % outputCount;
      store.setPlayhead(next);
    },
    [outputCount, playhead, store],
  );

  const cutAtPlayhead = useCallback(() => {
    const abs = outputIndices[playhead];
    if (abs !== undefined) store.cutAtPlayhead(abs);
  }, [outputIndices, playhead, store]);

  // Das ROHE Output-Frame an der aktuellen Stelle (vor allen Effekten) — Fallback-Anzeige UND
  // „Original"-Seite der Shift-Vergleichslupe.
  const originalFrame: ImageData | null = useMemo(() => {
    if (outputCount === 0) return null;
    const abs = outputIndices[Math.min(playhead, outputCount - 1)];
    return abs === undefined ? null : (framesRef.current[abs] ?? null);
    // framesRef ist ein Ref → renderTick triggert die Neuberechnung
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputCount, playhead, outputIndices, renderTick]);

  // Aktueller Anzeige-Frame (editiert).
  const displayFrame: ImageData | null = useMemo(() => {
    if (outputCount === 0) return null;
    const pv = previewRef.current;
    if (hasEnabledEffects && pv && pv.length > 0) {
      // Playhead (Quell-Output-Index) proportional in die Effekt-Sequenz abbilden (speed).
      return pv[mapProportional(playhead, outputCount, pv.length)] ?? null;
    }
    return originalFrame;
    // previewRef ist ein Ref → renderTick (Bump nach jedem fertigen Preview) triggert die
    // Neuberechnung — sonst aktualisiert die Vorschau erst beim Abspielen (playhead-Änderung).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputCount, hasEnabledEffects, playhead, originalFrame, renderTick]);

  // ── Preis-Vorschau ──────────────────────────────────────────────────────────
  const steps = useMemo(
    () => deriveSteps({ totalFrames, segments, resultKind, effects: flatEffects, seam }),
    [totalFrames, segments, resultKind, flatEffects, seam],
  );
  // Codex-P1-1: leere Auswahl (alle Segmente gelöscht) ⇒ kein speicherbarer Output ⇒ credits null
  // (Save-Button disabled). Sonst exportierte ein „alles weg"-Zustand das Vollmaterial.
  const hasOutput = outputCount > 0;
  const credits = useMemo(
    () => (steps.length === 0 || !hasOutput ? null : estimateStudioEditCredits(steps)),
    [steps, hasOutput],
  );

  // Kopf des Effekt-Panels: „Clip N · 0,8 s" des selektierten (behaltenen) Clips.
  const selectedClipLabel = useMemo(() => {
    if (!selectedSegmentId) return null;
    const kept = segments.filter((s) => s.keep);
    const i = kept.findIndex((s) => s.id === selectedSegmentId);
    if (i < 0) return null;
    const seg = kept[i]!;
    const len = Math.max(0, Math.min(seg.to, totalFrames) - seg.from);
    const secs = len / Math.max(1, fps);
    return `Clip ${i + 1} · ${(secs < 1 ? secs.toFixed(2) : secs.toFixed(1)).replace(".", ",")} s`;
  }, [selectedSegmentId, segments, totalFrames, fps]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!sourceAssetId) {
      setNotice("Keine Quell-Asset-ID — bitte erneut laden.");
      return;
    }
    if (steps.length === 0) {
      setNotice("Nichts zu speichern (keine Änderung).");
      return;
    }
    if (outputFrameCount(segments, totalFrames) === 0) {
      setNotice("Leere Auswahl — bitte mindestens ein Segment behalten.");
      return;
    }
    setBusy(true);
    setNotice(null);
    const ops = getBrowserOps();
    const withSeam = resultKind === "spritesheet" && seam && loop;
    // D169: Phasen-Plan für den ProgressBar. Sheet+Naht = 4 (Naht rendern → glätten → packen →
    // speichern), Sheet ohne Naht = 2 (packen → speichern), Einzelframe = 2 (rendern → speichern).
    // `value` = NUMMER des GERADE laufenden Schritts (1-indexiert), nicht die abgeschlossenen ⇒ der
    // Zähler läuft 1→max und der Balken ist beim letzten (Netzwerk-)Schritt voll (kein „nie 100 %").
    const max = withSeam ? 4 : 2;
    try {
      const ranges = keptRanges(segments, totalFrames);
      const baseReq = {
        editKind: "studio" as const,
        sourceAssetId,
        opsHash: opsHashRef.current,
        params: activeKeyParams(flatEffects), // Provenance: keyColor/tolerance des ersten aktiven chroma-key-Effekts
        segments: ranges,
        sourceFps: fps,
        effects: flatEffects, // alle Clip-Effekte flach als Provenance (preis-neutral, in den PNG-Bytes gebacken)
        folderId: selectedFolderId ?? null,
      };
      if (resultKind === "spritesheet") {
        const clips = buildClipGroups(segments, totalFrames, { withEffects: true });
        let seamPng: Uint8Array | undefined;
        if (withSeam) {
          // RIFE-Naht über die EFFEKTIERTEN+gecroppten Output-Randframes (end→start): so passt der
          // Naht-Frame visuell zur Sequenz (gekeyt/transparent) UND größengleich. fraction 1/0 =
          // letztes/erstes Frame der ECHTEN Länge (race-frei gegen speed); SELBE cropTransparent-
          // Pipeline ⇒ identische Maße zur Pack-Sequenz (frames_append resized nicht).
          setProgress({ label: "Naht rendern…", value: 1, max });
          const endPng = await ops.studioFramePng(clips, { fraction: 1 }, undefined, cropTransparent);
          const startPng = await ops.studioFramePng(clips, { fraction: 0 }, undefined, cropTransparent);
          setProgress({ label: "Naht glätten…", value: 2, max });
          seamPng = await api.seamFrame(sourceAssetId, endPng, startPng);
        }
        setProgress({ label: "Sheet packen…", value: withSeam ? 3 : 1, max });
        const { png, meta } = await ops.studioPackSheet(clips, fps, loop, seamPng, cropTransparent);
        setProgress({ label: "In Bibliothek speichern…", value: withSeam ? 4 : 2, max });
        const req: EditRequest = { ...baseReq, steps, resultKind: "spritesheet", packMeta: meta };
        const res = await api.submitEdit(png, req);
        setNotice(res.replayed ? "Bereits gespeichert (0 cr)." : `Gespeichert (${res.chargedCredits} cr).`);
      } else {
        const clips = buildClipGroups(segments, totalFrames, { withEffects: true });
        // Codex-P1 (R2): die proportionale Abbildung löst der WORKER gegen die ECHTE Effekt-Länge
        // auf (race-frei — unabhängig davon, ob die debounced Vorschau schon materialisiert ist).
        const srcN = outputFrameCount(segments, totalFrames);
        setProgress({ label: "Frame rendern…", value: 1, max });
        const png = await ops.studioFramePng(clips, { fraction: srcN <= 1 ? 0 : playhead / (srcN - 1) }, undefined, cropTransparent);
        setProgress({ label: "In Bibliothek speichern…", value: 2, max });
        const req: EditRequest = { ...baseReq, steps, resultKind: "image" };
        const res = await api.submitEdit(png, req);
        setNotice(res.replayed ? "Bereits gespeichert (0 cr)." : `Gespeichert (${res.chargedCredits} cr).`);
      }
    } catch (e) {
      setNotice(`Speichern fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }, [sourceAssetId, steps, segments, totalFrames, flatEffects, cropTransparent, fps, selectedFolderId, resultKind, seam, loop, playhead]);

  // Lokaler Download (kein Server, gratis): erzeugt dasselbe Pixel-Ergebnis wie der Save (Sheet-PNG
  // bzw. Einzelframe-PNG) und lädt es als Datei herunter. Die RIFE-Naht ist NICHT enthalten
  // (server-only, wird nur beim „Speichern" berechnet) — der Download ist der rohe Komposit.
  const download = useCallback(async () => {
    if (outputFrameCount(segments, totalFrames) === 0) {
      setNotice("Leere Auswahl — bitte mindestens ein Segment behalten.");
      return;
    }
    setBusy(true);
    setNotice(null);
    const ops = getBrowserOps();
    try {
      const clips = buildClipGroups(segments, totalFrames, { withEffects: true });
      const srcN = outputFrameCount(segments, totalFrames);
      setProgress({ label: resultKind === "spritesheet" ? "Sheet packen…" : "Frame rendern…", value: 1, max: 2 });
      const png =
        resultKind === "spritesheet"
          ? (await ops.studioPackSheet(clips, fps, loop, undefined, cropTransparent)).png
          : await ops.studioFramePng(clips, { fraction: srcN <= 1 ? 0 : playhead / (srcN - 1) }, undefined, cropTransparent);
      setProgress({ label: "Datei erzeugen…", value: 2, max: 2 });
      const url = URL.createObjectURL(new Blob([png.slice().buffer as ArrayBuffer], { type: "image/png" }));
      downloadResult(url, resultFilename(resultKind, sourceAssetId ?? "studio", "png"));
      setTimeout(() => URL.revokeObjectURL(url), 10_000); // nach dem synchronen Klick freigeben
      setNotice("Heruntergeladen.");
    } catch (e) {
      setNotice(`Download fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }, [segments, totalFrames, resultKind, fps, loop, playhead, sourceAssetId, cropTransparent]);

  const exportWebm = useCallback(async () => {
    if (!sourceAssetId) return;
    if (outputFrameCount(segments, totalFrames) === 0) {
      setNotice("Leere Auswahl — bitte mindestens ein Segment behalten.");
      return;
    }
    setBusy(true);
    setNotice(null);
    // Ein einzelner Netzwerk-Schritt unbekannter Dauer ⇒ indeterminate (value weggelassen). Die
    // eigentliche WebM-Codierung läuft danach als Job (sichtbar in der Jobs-Liste, eigener Balken).
    setProgress({ label: "WebM-Job starten…" });
    try {
      // WebM-Job (v1) bildet nur Schnitt + chroma-key server-seitig nach (der Button ist sonst
      // gesperrt). keyColor/tolerance kommen aus dem aktiven chroma-key-Effekt (über alle Clips).
      const key = activeKeyParams(flatEffects);
      const hasKey = flatEffects.some((e) => e.enabled && e.type === "chroma-key");
      await api.submitJob({
        toolId: "studio-export",
        settings: { segments: keptRanges(segments, totalFrames), fps, removeBackground: hasKey, tolerance: key.tolerance, keyColor: key.keyColor },
        inputs: [{ assetId: sourceAssetId, role: "source" }],
        targetFolderId: selectedFolderId ?? null,
      });
      setNotice("WebM-Export gestartet (Job).");
    } catch (e) {
      setNotice(`WebM-Export fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }, [sourceAssetId, segments, totalFrames, fps, flatEffects, selectedFolderId]);

  const applyGrid = useCallback(
    async (g: { cols: number; rows: number; frameCount: number }) => {
      setGridDialogOpen(false);
      const asset = sourceAssetId;
      // Re-Unpack des Original-Sheets mit dem neuen Raster. Quelle ist der schon geladene Blob
      // (kein zweiter Server-Fetch — das war bei großen Sheets der langsame Teil); nur als Fallback
      // (Bytes nicht mehr vorhanden) erneut über den Bytes-Proxy holen.
      const cached = originalBlobRef.current;
      if (!cached && !asset) return;

      // Wie loadFromBlob: laufende Operation abbrechen + diesen Lauf gegen Überholer/Unmount schützen.
      const ac = new AbortController();
      loadAbortRef.current?.abort();
      loadAbortRef.current = ac;
      const { signal } = ac;

      // Sichtbares Feedback: applyGrid lief bisher OHNE Status/Spinner → bei großen Sheets wirkte das
      // sekundenlange Re-Unpack wie „passiert nichts". Jetzt Vollbild-Spinner (wie der Erst-Load).
      store.setStatus("loading");
      setBootStage("Wende Raster an…");
      setNotice(null);
      const o = getBrowserOps();
      try {
        const blob = cached ?? (await api.assetBytes(asset!, signal));
        const img = await blobToImageData(blob);
        if (signal.aborted) return;
        // Codex-P0: Abort-Guard VOR JEDEM mutierenden Worker-Call. Der browser-ops-Client serialisiert
        // nur einzelne Methoden (FIFO-`enqueue`), nicht die ganze Re-Unpack-Transaktion. Ein Überholer
        // ruft an SEINEM Start synchron `loadAbortRef.current?.abort()` → unser `signal` ist dann
        // gesetzt, bevor wir den nächsten Schritt einreihen ⇒ wir reihen KEINE weitere `st-begin`/
        // `st-unpack` mehr ein, die seinen frisch geladenen Worker-Puffer überschreiben würde. Sein
        // eigener `studioLoad`(st-begin) räumt einen bereits laufenden Schritt von uns ohnehin weg.
        await o.studioLoad([img]);
        if (signal.aborted) return;
        await o.studioUnpack(g.cols, g.rows, g.frameCount);
        if (signal.aborted) return;
        const frames = await o.studioPreview([], undefined);
        if (signal.aborted) return;
        await o.studioLoad(frames);
        if (signal.aborted) return;
        originalBlobRef.current = blob; // (falls über Fallback geholt) für die nächste Korrektur behalten
        framesRef.current = frames;
        const first = frames[0]!;
        store.loaded({
          sourceAssetId: asset,
          sourceKind: sourceKind ?? "spritesheet",
          totalFrames: frames.length,
          frameWidth: first.width,
          frameHeight: first.height,
          fps,
          grid: { cols: g.cols, rows: g.rows, frameCount: g.frameCount, method: "alpha", detectedKey: grid?.detectedKey ?? null },
          resultKind: "spritesheet",
          loop: true,
        });
        forceRender((n) => n + 1);
      } catch (e) {
        // Codex-P0: ein abgebrochener Lauf (Unmount/neuer Load) darf weder Status noch Worker-Puffer
        // der frisch gestarteten Operation überschreiben — auch NICHT durch das Restore unten.
        if (signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        // st-begin (in studioLoad) hat den Puffer bereits auf das Original-Sheet zurückgesetzt: mit den
        // AKTUELLEN Frames neu füllen, damit Vorschau/Save konsistent bleiben. Der Fehler (z.B. das
        // 100-MPx-Limit eines zu großen Sheets) wird so SICHTBAR statt verschluckt.
        const hadFrames = framesRef.current.length > 0;
        const restored = hadFrames && (await o.studioLoad(framesRef.current).then(() => true).catch(() => false));
        // Codex-P0: nach dem async Restore erneut prüfen — ein Überholer kann WÄHREND des `await`
        // übernommen haben; dann gehört ihm der State, wir fassen ihn nicht an.
        if (signal.aborted) return;
        if (restored) {
          // Bisherige Sequenz bleibt bearbeitbar.
          store.setStatus("ready");
          setNotice(`Raster anwenden fehlgeschlagen: ${msg}`);
        } else {
          // Codex-P1: ließ sich der Puffer NICHT wiederherstellen (Restore-Fail / nichts zu
          // restaurieren), wäre 'ready' eine Lüge — Vorschau/Save würden mit „keine Frames" scheitern.
          // Ehrlich auf 'empty' zurück (Dropzone), der Fehler steht als Status-Meldung.
          // Codex-R2-P2: der Empty-State hat keinen Schließen-Button ⇒ hier selbst die gehaltenen
          // Original-Bytes freigeben (sonst hingen sie bis Unmount/Neuladen im Speicher).
          originalBlobRef.current = null;
          store.setStatus("empty", `Raster anwenden fehlgeschlagen: ${msg}`);
        }
      }
    },
    [sourceAssetId, sourceKind, fps, grid, store],
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  if (status === "empty") {
    return (
      <div className="grid h-full w-full place-items-center p-8">
        {/* Die ganze Box ist die Dropzone: Datei ziehen & ablegen ODER den Picker klicken (gleicher
            `onUpload`-Pfad). Drag-Feedback wie die §11-Media-Dropzones (D61, border-accent). */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = firstMediaFile(e.dataTransfer.files);
            if (f) void onUpload(f);
            else if (e.dataTransfer.files.length) store.setStatus("empty", "Nur Bilder oder Videos werden unterstützt.");
          }}
          className={`flex w-full max-w-md flex-col items-center gap-4 rounded-xl2 border border-dashed p-10 text-center transition ${
            dragging ? "border-accent bg-accent/10" : "border-border bg-surface"
          }`}
        >
          <h2 className="text-lg font-semibold text-text">Asset-Studio</h2>
          <p className="text-sm text-muted">
            Ziehe ein Bild, Video oder Spritesheet hierher oder wähle eine Datei — schneiden,
            Hintergrund entfernen, als Spritesheet/Einzelframe (oder WebM) speichern. Alles live im
            Browser.
          </p>
          {error && <p className="text-sm text-danger">{error}</p>}
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUpload(f);
              }}
            />
            <span className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white">
              Datei wählen
            </span>
          </label>
        </div>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="grid h-full w-full place-items-center p-8">
        <div className="flex w-56 max-w-full flex-col items-center gap-3 text-muted">
          <Spinner />
          <p className="text-sm">{bootStage || "Lade Quelle…"}</p>
          {/* D169: indeterminate ProgressBar — der Boot (ensureReady/Pyodide, Decode) hat keinen
              zählbaren Restwert; das laufende Segment signalisiert „arbeitet weiter". */}
          <ProgressBar size="md" label={bootStage || "Lade Quelle…"} />
        </div>
      </div>
    );
  }

  // WebM-Export (Job) gibt es nur für Video-Quellen UND nur, solange der Stack server-IDENTISCH
  // nachbildbar ist (Schnitt + chroma-key). Andere aktive Effekte sperren ihn (Hinweis im Panel).
  const sourceIsVideo = sourceKind === "video" || sourceKind === "animation";
  // WebM nur, wenn der Job die Pro-Clip-Komposition global reproduzieren kann (Schnitt + EIN global
  // konsistenter chroma-key, keine sonstigen Effekte) — sonst würde das Ergebnis von der Vorschau
  // abweichen (Codex-P1: clip-lokaler Key/gemischtes Keyen wird vom Job global angewandt).
  const canExportWebm = sourceIsVideo && hasOutput && effectsAllowWebm(keptClipEffects);
  const webmBlockedByEffects = sourceIsVideo && hasOutput && !effectsAllowWebm(keptClipEffects);

  return (
    <div className="flex h-full w-full flex-col">
      {/* Kopfleiste */}
      <div className="flex items-center gap-2 border-b border-border bg-surface px-3 py-2">
        <span className="text-sm font-semibold text-text">Studio</span>
        {/* Undo/Redo — gilt für ALLE Timeline-/Effekt-Edits (Buttons + ⌘Z/⌘⇧Z). disabled, wenn der
            jeweilige History-Stack leer ist. */}
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => store.undo()}
            disabled={store.past.length === 0}
            className="grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-text disabled:opacity-30 disabled:hover:bg-transparent"
            title="Rückgängig (⌘Z)"
            aria-label="Rückgängig"
          >
            <Icon name="undo" size={16} />
          </button>
          <button
            type="button"
            onClick={() => store.redo()}
            disabled={store.future.length === 0}
            className="grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-text disabled:opacity-30 disabled:hover:bg-transparent"
            title="Wiederholen (⌘⇧Z)"
            aria-label="Wiederholen"
          >
            <Icon name="redo" size={16} />
          </button>
        </div>
        {/* Codex-P1-2: das Badge ist für JEDE Nicht-Video-Quelle da (auch method='none') — es ist
            der Heil-/Eingabe-Weg zum manuellen Grid; nur Video/Animation haben kein Grid-Konzept. */}
        {grid && sourceKind !== "video" && sourceKind !== "animation" && (
          <GridBadge cols={grid.cols} rows={grid.rows} method={grid.method} onClick={() => setGridDialogOpen(true)} />
        )}
        {notice && <span className="ml-2 text-xs text-muted">{notice}</span>}
        <div className="ml-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSheet(null);
              originalBlobRef.current = null; // Codex-P2: gehaltene Original-Bytes mit dem Schließen freigeben
              store.reset();
            }}
          >
            Schließen
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1">
            {/* original = rohes Frame (vor Effekten) für die Shift-Lupe; bei Effekten ≠ displayFrame
                → echter Split-Vergleich, ohne Effekte == displayFrame → reines Zoom-Glas. */}
            <StudioCanvas frame={displayFrame} original={originalFrame} />
          </div>
          <TransportBar
            playing={playing}
            index={playhead}
            total={outputCount}
            fps={fps}
            onTogglePlay={() => store.setPlaying(!playing)}
            onStep={step}
          />
          <Timeline
            segments={segments}
            totalFrames={totalFrames}
            outputCount={outputCount}
            playhead={playhead}
            fps={fps}
            frames={framesRef.current}
            selectedId={selectedSegmentId}
            onSelect={store.setSelectedSegment}
            onScrub={(i) => {
              store.setPlaying(false);
              store.setPlayhead(i);
            }}
            onCut={cutAtPlayhead}
            onTrim={store.trimSegment}
            onReorder={store.moveSegment}
            onDelete={(id) => store.toggleKeep(id, false)}
            onRestore={(id) => store.toggleKeep(id, true)}
          />
        </div>

        <OpStackPanel
          effects={selectedEffects}
          selectedClipLabel={selectedClipLabel}
          hasSelection={selectedSegmentId !== null}
          onAddEffect={store.addStudioEffect}
          onRemoveEffect={store.removeStudioEffect}
          onToggleEffect={store.toggleStudioEffect}
          onUpdateEffect={store.updateStudioEffect}
          onReorderEffect={store.moveStudioEffect}
          resultKind={resultKind}
          onResultKind={store.setResultKind}
          loop={loop}
          onLoop={store.setLoop}
          seam={seam}
          onSeam={store.setSeam}
          canExportWebm={canExportWebm}
          webmBlockedByEffects={webmBlockedByEffects}
          canDownload={hasOutput}
          credits={credits}
          busy={busy}
          progress={progress}
          onSave={save}
          onDownload={download}
          onExportWebm={exportWebm}
        />
      </div>

      {gridDialogOpen && grid && (
        <GridDialog
          initial={{ cols: grid.cols, rows: grid.rows, frameCount: grid.frameCount }}
          sheet={sheet}
          onApply={(g) => void applyGrid(g)}
          onCancel={() => setGridDialogOpen(false)}
        />
      )}
    </div>
  );
}
